import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheck, FiMail, FiFileText, FiArrowRight } from "react-icons/fi";

export default function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { processResponse, driver } = location.state || {};

  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    const reservationNumber = processResponse?.reservation_number;
    if (!reservationNumber || purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;

    const {
      vendor_name: vendorName,
      customer_name: customerNameFromApi,
      total_amount: totalAmount,
      currency,
      itinerary,
      booking_date_utc: bookingDateUtc,
      length_of_rental: lengthOfRental,
      customer_city: customerCity,
      customer_state: customerState,
    } = processResponse;

    const customerName =
      customerNameFromApi ||
      (driver
        ? `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim()
        : undefined);

    const bookingDate = bookingDateUtc
      ? bookingDateUtc.slice(0, 10)
      : undefined;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        transaction_id: reservationNumber,
        value: totalAmount,
        currency,
        items: [
          {
            item_name: "Car Rental",
            ...(vendorName ? { item_brand: vendorName } : {}),
            price: totalAmount,
            quantity: 1,
          },
        ],
      },
      // Custom Rental Parameters
      ...(customerName ? { customer_name: customerName } : {}),
      ...(vendorName ? { vendor_name: vendorName } : {}),
      ...(itinerary?.pickup_location
        ? { pickup_location: itinerary.pickup_location }
        : {}),
      ...(itinerary?.dropoff_location
        ? { dropoff_location: itinerary.dropoff_location }
        : {}),
      ...(bookingDate ? { booking_date: bookingDate } : {}),
      ...(lengthOfRental !== undefined && lengthOfRental !== null
        ? { length_of_rental: lengthOfRental }
        : {}),
      ...(customerCity ? { customer_city: customerCity } : {}),
      ...(customerState ? { customer_state: customerState } : {}),
    });
  }, [processResponse, driver]);

  if (!processResponse) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <button
          onClick={() => navigate("/")}
          className="bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-xl font-heading font-bold transition-colors"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-raised text-text font-body antialiased mt-12">
      <main className="max-w-6xl mx-auto pt-10 pb-12 px-6">
        {/* Success Icon & Main Title */}
        <div className="flex flex-col items-center text-center mt-10 mb-6">
          <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-5 shadow-card">
            <FiCheck className="text-white text-2xl" />
          </div>

          <h1 className="text-3xl md:text-5xl font-heading font-black text-text mb-4 tracking-tight">
            Reservation Successful!
          </h1>

          <div className="text-text-muted text-lg md:text-xl leading-snug font-body">
            <p className="mb-0.5">
              Dear{" "}
              <span className="font-semibold text-text">
                {driver?.firstName} {driver?.lastName}
              </span>
              ,
            </p>
            <p>
              Your booking has been placed successfully. Your reservation number
              is{" "}
              <span className="font-bold text-primary-hover">
                {processResponse.reservation_number}
              </span>
            </p>
          </div>
        </div>

        {/* Action Content */}
        <div className="max-w-5xl mx-auto border-t border-surface-border pt-10">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* Left Column: Email Info */}
            <div className="flex flex-col items-center text-center px-8">
              <div className="bg-accent p-4 rounded-full mb-4">
                <FiMail className="text-accent-foreground text-2xl" />
              </div>
              <p className="text-text-muted text-base leading-relaxed font-body">
                A copy of your booking and receipt has been sent to your
                registered email address.
              </p>
            </div>

            {/* Right Column: Voucher Info & Action */}
            <div className="flex flex-col items-center text-center px-8 border-l border-surface-border">
              <div className="bg-accent p-4 rounded-full mb-4">
                <FiFileText className="text-accent-foreground text-2xl" />
              </div>
              <p className="text-text-muted text-base leading-relaxed mb-6 font-body">
                Download or access your rental voucher via manage booking
              </p>
              <button
                onClick={() => navigate("/manage-booking")}
                className="flex items-center gap-2 px-8 py-3 bg-primary hover:bg-primary-hover text-white rounded-full font-heading font-bold text-xs uppercase tracking-widest transition-colors shadow-card"
              >
                Manage Booking <FiArrowRight />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <p className="text-[10px] text-text-light uppercase tracking-widest font-heading font-bold">
            Need Help?{" "}
            <span
              className="text-primary cursor-pointer hover:text-primary-hover hover:underline ml-1"
              onClick={() => navigate("/support")}
            >
              Contact Support
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}